import { useEffect, useRef, forwardRef, useState, useCallback } from "react";
import { GOOGLE_MAPS_API_KEY } from "./../constants";
import { Loader } from "@googlemaps/js-api-loader";
import "./DroneMap.css";
import DroneTracker from "./DroneTracker";

import { useDispatch, useSelector } from "react-redux";
import axios from "../../lib/http/axiosConfig";
import { NDVIToggle } from "../Map/NDVIToggle";
import { setToggle, setCellPreviewOpen } from "../../features/TabSlice";
import { toPath } from "../../utils/map";
import calendarIcon from "../../Styles/icons/calendar.png";
import { NDVIDateModal } from "../Map/NDVIDateModal";
import { toast } from "react-toastify";
import { parseUTC } from "../../utils/date";

const defaultCenter = { lat: 35.222172, lng: 126.847596 };
const defaultZoom = 17;
const LOCK_MAP_INTERACTION = true;
const LOCKED_ZOOM_FALLBACK = 19;
const getResponsiveZoom = (width) => {
  if (!Number.isFinite(width)) return LOCKED_ZOOM_FALLBACK;
  if (width >= 1600) return 19;
  if (width >= 1200) return 17;
  if (width >= 900) return 20;
  return 15;
};
const FARM_POLYGON_STYLE = {
  strokeColor: "#FFFFFF",
  strokeOpacity: 1,
  strokeWeight: 3,
  fillColor: "#000000",
  fillOpacity: 0.25,
  clickable: false,
  zIndex: 0,
};
const FARM_POLYGON_STYLE_ANALYSIS = {
  ...FARM_POLYGON_STYLE,
  fillColor: "#4A84C7",
  fillOpacity: 0.5,
};
const GRID_CELL_STYLE = {
  strokeColor: "#0C1620",
  strokeOpacity: 0,
  strokeWeight: 0,
  fillColor: "#000000",
  fillOpacity: 0.01,
  clickable: true,
  zIndex: 1,
};
const GRID_CELL_SELECTED_STYLE = {
  fillColor: "#39D2B4",
  fillOpacity: 0.4,
  strokeColor: "#7FF5D0",
  strokeOpacity: 0.9,
  zIndex: 2,
};
const GRID_CELL_SELECTED_STYLE_ANALYSIS = {
  ...GRID_CELL_SELECTED_STYLE,
  fillOpacity: 0.5,
  strokeOpacity: 1,
};
const GRID_CELL_HOVER_STYLE = {
  fillColor: "#39D2B4",
  fillOpacity: 0.22,
  strokeColor: "#B8FFE9",
  strokeOpacity: 0.95,
  strokeWeight: 2,
  zIndex: 3,
};
const SELECTABLE_MAX_ZOOM = 22;

const DroneMap = forwardRef((props, ref) => {
  const {
    onMapClick,
    onCellClick,
    selectedCellIds,
    mapRef,
  } = props;
  const mapElementRef = useRef(null);

  const dispatch = useDispatch();
  const farmId = useSelector((state) => state.farm.farmId);
  const toggle = useSelector((state) => state.tab.toggle);
  const boundary = useSelector((state) => state.farm.boundary);
  const gridCells = useSelector((state) => state.farm.gridCells);

  useEffect(() => {
    dispatch(setToggle(false));
  }, [farmId]);

  const [isMapReady, setIsMapReady] = useState(false);
  const gridPolygonsRef = useRef([]);
  const gridLinesRef = useRef([]);
  const farmPathRef = useRef([]);


  const isPointInside = (point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lat;
      const yi = polygon[i].lng;
      const xj = polygon[j].lat;
      const yj = polygon[j].lng;
      const intersect =
        yi > point.lng !== yj > point.lng &&
        point.lat < ((xj - xi) * (point.lng - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const applyLockedZoom = useCallback((map) => {
    if (!map || !LOCK_MAP_INTERACTION) return;
    const responsiveZoom = getResponsiveZoom(mapElementRef.current?.offsetWidth);
    if (Number.isFinite(responsiveZoom)) {
      map.setZoom(responsiveZoom);
    }
  }, []);

  const [isDateOpen, setIsDateOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [isCellImageOpen, setIsCellImageOpen] = useState(false);
  const [isCellPreviewOpen, setIsCellPreviewOpen] = useState(false);
  const [cellImageUrl, setCellImageUrl] = useState("");
  const [cellThumbUrl, setCellThumbUrl] = useState("");
  const [isCellImageLoading, setIsCellImageLoading] = useState(false);
  const [cellPreviewList, setCellPreviewList] = useState([]);
  const [cellPreviewIndex, setCellPreviewIndex] = useState(0);
  const [isGradCam, setIsGradCam] = useState(false);
  const [cellStatusLabel, setCellStatusLabel] = useState("");
  const [cellStatusNote, setCellStatusNote] = useState("");
  const [cellStatusTone, setCellStatusTone] = useState("warning");
  const [cellCoordText, setCellCoordText] = useState("");
  const [selectedMissionLogId, setSelectedMissionLogId] = useState(null);
  const [selectedMissionTime, setSelectedMissionTime] = useState("");
  const [missionDates, setMissionDates] = useState(new Set());
  const hasMissionDates = missionDates.size > 0;
  const [missionTimeList, setMissionTimeList] = useState([]);
  const [isTimeListOpen, setIsTimeListOpen] = useState(false);
  const [hasAppliedDate, setHasAppliedDate] = useState(false);
  const [analysisCellIds, setAnalysisCellIds] = useState([]);
  const [isAnalysisAvailable, setIsAnalysisAvailable] = useState(false);

  useEffect(() => {
    dispatch(setCellPreviewOpen(isCellPreviewOpen));
    return () => dispatch(setCellPreviewOpen(false));
  }, [isCellPreviewOpen, dispatch]);

  useEffect(() => {
    if (!farmId) return;
  }, [farmId]);

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!toggle) return;
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [toggle]);

  const formatNow = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${y}.${m}.${day} ${hh}:${mm}:${ss}`;
  };

  const formatSelectedDate = (d) => {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}.${m}.${day}`;
  };

  const formatTime = (d) => {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };

  const formatDateKey = (d) => {
    const local = new Date(d);
    local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
    return local.toISOString().slice(0, 10);
  };

  const fetchMissionLogForDate = useCallback(async () => {
    if (!farmId) return null;
    const targetDate = formatDateKey(selectedDate);
    const { data } = await axios.get(`/api/farms/${farmId}/mission-logs`, {
      params: { date: targetDate },
    });
    const content = data?.data?.content ?? [];
    if (!content.length) {
      setSelectedMissionLogId(null);
      setSelectedMissionTime("");
      setMissionTimeList([]);
      return null;
    }
    const matched = content
      .map((item) => ({
        id: item.id,
        date: parseUTC(item.missionDate),
      }))
      .filter((item) => Number.isFinite(item.date?.getTime?.()));

    if (!matched.length) {
      setSelectedMissionLogId(null);
      setSelectedMissionTime("");
      setMissionTimeList([]);
      return null;
    }

    matched.sort((a, b) => a.date - b.date);
    const times = matched.map((item) => ({
      id: item.id,
      time: formatTime(item.date),
      label: `${formatSelectedDate(item.date)} ${formatTime(item.date)}`,
    }));
    setMissionTimeList(times);

    const first = matched[0];
    setSelectedMissionLogId(first.id);
    setSelectedMissionTime(formatTime(first.date));
    return first.id;
  }, [farmId, selectedDate]);

  useEffect(() => {
    if (!toggle || !farmId) {
      setMissionDates(new Set());
      return;
    }
    const fetchMissionDates = async () => {
      try {
        const { data } = await axios.get(`/api/farms/${farmId}/mission-dates`);
        const list = Array.isArray(data?.data) ? data.data : [];
        setMissionDates(new Set(list.filter(Boolean)));
      } catch {
        setMissionDates(new Set());
      }
    };
    fetchMissionDates();
  }, [toggle, farmId]);

  const fetchAnalysisCellIds = useCallback(async () => {
    if (!farmId || !toggle) return;
    try {
      const missionLogId =
        selectedMissionLogId ?? (await fetchMissionLogForDate());
      if (!missionLogId) {
        setAnalysisCellIds([]);
        setIsAnalysisAvailable(false);
        return;
      }

      const { data } = await axios.get(
        `/api/farms/${farmId}/missions/${missionLogId}/cell-status`
      );
      const ids = data?.data?.redCellIds ?? [];
      setAnalysisCellIds(ids);
      setIsAnalysisAvailable(true);
    } catch (error) {
      console.error("Failed to load cell status:", error);
      setAnalysisCellIds([]);
      setIsAnalysisAvailable(false);
    }
  }, [farmId, toggle, fetchMissionLogForDate, selectedMissionLogId]);

  useEffect(() => {
    if (!toggle) return;
    fetchAnalysisCellIds();
  }, [toggle, selectedDate, fetchAnalysisCellIds]);

  const openCellImage = useCallback(
    async (cell) => {
      if (!toggle || !cell?.id || !farmId) return;
      setIsCellImageLoading(true);
      setIsCellPreviewOpen(false);
      try {
        const missionLogId =
          selectedMissionLogId ?? (await fetchMissionLogForDate());
        if (!missionLogId) return;

        const { data } = await axios.get(
          `/api/farms/${farmId}/mission-logs/${missionLogId}/grid-cells/${cell.id}/diagnosis-results`,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (data?.success === false || data?.isAvailable === false) {
          setCellImageUrl("");
          setCellThumbUrl("");
          setCellPreviewList([]);
          setCellPreviewIndex(0);
          setIsGradCam(false);
          setCellStatusLabel("");
          setCellStatusNote("");
          setCellStatusTone("warning");
          setCellCoordText("");
          toast.warn("No images available yet. Photos may still be uploading.");
          return;
        }

        const payload = data?.data ?? {};
        const segments = payload.segments ?? [];
        const photoUrl = payload.photo?.imageUrl || "";
        const previewItems = segments
          .map((segment) => ({
            crop: segment.cropImageUrl || "",
            gradcam: segment.gradcamImageUrl || "",
          }))
          .filter((item) => item.crop || item.gradcam);

        if (!previewItems.length && photoUrl) {
          previewItems.push({ crop: photoUrl, gradcam: "" });
        }

        // previewItems를 3번째(index 2)부터 시작하도록 재배열
        if (previewItems.length >= 3) {
          const reordered = [
            ...previewItems.slice(2),
            ...previewItems.slice(0, 2),
          ];
          previewItems.splice(0, previewItems.length, ...reordered);
        }

        // 첫 화면은 합성 이미지(photo.imageUrl), 세부는 개별 이미지(segments)
        const imageUrl = photoUrl || (previewItems[0]?.crop ?? "");
        const statusLabel =
          payload?.diagnosis?.label ||
          payload?.statusLabel ||
          payload?.diseaseLabel ||
          "";
        const statusNote =
          payload?.diagnosis?.summary ||
          payload?.statusNote ||
          payload?.diseaseNote ||
          "";
        const statusTone =
          payload?.diagnosis?.tone ||
          payload?.statusTone ||
          payload?.tone ||
          "";
        const coordText =
          payload?.photo?.coordText || payload?.coordText || "";

        const showNoImage = () => {
          setCellImageUrl("");
          setCellThumbUrl("");
          setCellPreviewList([]);
          setCellPreviewIndex(0);
          setIsGradCam(false);
          setCellStatusLabel("");
          setCellStatusNote("");
          setCellStatusTone("warning");
          setCellCoordText("");
          toast.warn("No images available yet. Photos may still be uploading.");
        };

        if (!imageUrl) {
          showNoImage();
          return;
        }

        const imageReachable = await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = imageUrl;
        });

        if (!imageReachable) {
          showNoImage();
          return;
        }

        const normalizedTone = (() => {
          if (statusTone) return statusTone;
          if (statusLabel.includes("위험")) return "danger";
          if (statusLabel.includes("정상")) return "normal";
          if (statusLabel.includes("주의")) return "warning";
          if (statusLabel.includes("확률")) return "danger";
          return "warning";
        })();

        setCellPreviewList(previewItems);
        setCellPreviewIndex(0);
        setIsGradCam(false);
        setCellImageUrl(photoUrl);
        setCellThumbUrl(previewItems[0]?.crop || photoUrl);
        setCellStatusLabel(statusLabel || "");
        setCellStatusNote(statusNote);
        setCellStatusTone(normalizedTone);
        setCellCoordText(coordText);
        setIsCellImageOpen(true);
      } catch (error) {
        console.error("Failed to load cell image:", error);
        toast.warn("No images available yet. Photos may still be uploading.");
      } finally {
        setIsCellImageLoading(false);
      }
    },
    [toggle, farmId, fetchMissionLogForDate, selectedMissionLogId]
  );

  const handleSelectMissionTime = (item) => {
    if (!item?.id) return;
    setSelectedMissionLogId(item.id);
    setSelectedMissionTime(item.time);
    setIsTimeListOpen(false);
    fetchAnalysisCellIds();
  };

  const handleToggleTimeList = () => {
    setIsTimeListOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!toggle) setIsTimeListOpen(false);
  }, [toggle]);

  useEffect(() => {
    setIsTimeListOpen(false);
    setHasAppliedDate(false);
    setSelectedMissionLogId(null);
    setSelectedMissionTime("");
    setMissionTimeList([]);
  }, [selectedDate]);

  useEffect(() => {
    if (!toggle) setHasAppliedDate(false);
  }, [toggle]);

  useEffect(() => {
    setHasAppliedDate(false);
    setSelectedMissionLogId(null);
    setSelectedMissionTime("");
    setMissionTimeList([]);
  }, [farmId]);

  const polygonRef = useRef(null);
  const didFitRef = useRef(false);
  const farmBoundsRef = useRef(null);

  const fitToFarmOverview = useCallback(() => {
    const map = mapRef.current;
    const bounds = farmBoundsRef.current;
    if (!map || !bounds) return;

    map.setOptions({
      minZoom: 0,
      maxZoom: SELECTABLE_MAX_ZOOM,
      draggable: true,
      gestureHandling: "greedy",
      scrollwheel: true,
      disableDoubleClickZoom: false,
      keyboardShortcuts: true,
    });

    const mapWidth = mapElementRef.current?.offsetWidth || 0;
    const padding = Math.max(24, Math.round(mapWidth * 0.06));
    map.fitBounds(bounds, {
      top: padding,
      right: padding,
      bottom: padding,
      left: padding,
    });
    window.google.maps.event.addListenerOnce(map, "idle", () => {
      const z = map.getZoom();
      if (Number.isFinite(z)) {
        map.setOptions({
          minZoom: z,
          maxZoom: SELECTABLE_MAX_ZOOM,
        });
      }
    });
  }, [mapRef]);

  useEffect(() => {
    const map = mapRef.current;
    const path = toPath(boundary);
    farmPathRef.current = path;
    if (!map) return;

    if (path.length < 3) {
      farmBoundsRef.current = null;
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
      return;
    }

    if (!polygonRef.current) {
      polygonRef.current = new window.google.maps.Polygon({
        paths: path,
        map,
        ...FARM_POLYGON_STYLE,
      });
    } else {
      polygonRef.current.setPaths(path);
      polygonRef.current.setOptions(FARM_POLYGON_STYLE);
      if (!polygonRef.current.getMap()) polygonRef.current.setMap(map);
    }

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((p) => bounds.extend(p));
    farmBoundsRef.current = bounds;

    if (LOCK_MAP_INTERACTION) {
      map.setOptions({
        minZoom: 0,
        maxZoom: SELECTABLE_MAX_ZOOM,
        draggable: true,
        gestureHandling: "greedy",
        scrollwheel: true,
        disableDoubleClickZoom: false,
        keyboardShortcuts: true,
      });
      const mapWidth = mapElementRef.current?.offsetWidth || 0;
      const padding = Math.max(
        24,
        Math.round(mapWidth * 0.06)
      );
      map.fitBounds(bounds, {
        top: padding,
        right: padding,
        bottom: padding,
        left: padding,
      });
      window.google.maps.event.addListenerOnce(map, "idle", () => {
        const z = map.getZoom();
        if (Number.isFinite(z)) {
          map.setOptions({
            minZoom: z,
            maxZoom: SELECTABLE_MAX_ZOOM,
          });
        }
      });
    } else if (!didFitRef.current) {
      map.fitBounds(bounds);
      didFitRef.current = true;
    }

    return () => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, [boundary, mapRef, applyLockedZoom, toggle]);

  useEffect(() => {
    didFitRef.current = false;
  }, [farmId]);

  useEffect(() => {
    if (!polygonRef.current) return;
    polygonRef.current.setOptions(
      toggle ? FARM_POLYGON_STYLE_ANALYSIS : FARM_POLYGON_STYLE
    );
  }, [toggle]);

  useEffect(() => {
    if (!toggle) {
      setIsCellImageOpen(false);
      setIsCellPreviewOpen(false);
      setIsGradCam(false);
      setCellPreviewList([]);
      setCellPreviewIndex(0);
      setCellStatusLabel("");
      setCellStatusNote("");
      setCellStatusTone("warning");
      setCellCoordText("");
    }
  }, [toggle]);

  useEffect(() => {
    if (
      cellPreviewList.length > 0 &&
      cellPreviewIndex > cellPreviewList.length - 1
    ) {
      setCellPreviewIndex(0);
    }
  }, [cellPreviewList, cellPreviewIndex]);

  useEffect(() => {
    if (!mapRef.current || !isMapReady) return;

    gridPolygonsRef.current.forEach((poly) => poly.setMap(null));
    gridPolygonsRef.current = [];
    gridLinesRef.current.forEach((line) => line.setMap(null));
    gridLinesRef.current = [];

    if (!Array.isArray(gridCells) || gridCells.length === 0) return;

    const farmPath = farmPathRef.current;
    const hasFarmPath = Array.isArray(farmPath) && farmPath.length >= 3;
    const selectedIds = new Set(
      toggle ? analysisCellIds || [] : selectedCellIds || []
    );

    const dashSymbol = {
      path: "M 0,-1 0,1",
      strokeOpacity: 1,
      scale: 1,
    };

    gridCells.forEach((cell) => {
      const vertices = cell.vertices || [];
      if (vertices.length < 3) return;

      const center = cell.center;
      if (hasFarmPath && center) {
        const isInside = isPointInside(
          { lat: center.latitude, lng: center.longitude },
          farmPath
        );
        if (!isInside) return;
      }

      const paths = vertices.map((v) => ({
        lat: v.latitude,
        lng: v.longitude,
      }));
      const expandScale = 1.12;
      const expandedPaths = center
        ? paths.map((p) => ({
            lat: center.latitude + (p.lat - center.latitude) * expandScale,
            lng: center.longitude + (p.lng - center.longitude) * expandScale,
          }))
        : paths;

      const isSelected = selectedIds.has(cell.id);
      const isAnalysisSelectable =
        !toggle ||
        (isAnalysisAvailable && (!analysisCellIds.length || isSelected));
      const selectedStyle = isSelected
        ? toggle
          ? GRID_CELL_SELECTED_STYLE_ANALYSIS
          : GRID_CELL_SELECTED_STYLE
        : null;
      const baseCellStyle = {
        ...GRID_CELL_STYLE,
        clickable: isAnalysisSelectable,
        cursor: isAnalysisSelectable ? "pointer" : "default",
        ...(selectedStyle || null),
      };
      const polygon = new window.google.maps.Polygon({
        paths: expandedPaths,
        map: mapRef.current,
        ...baseCellStyle,
      });

      const handleCellSelect = () => {
        if (toggle) {
          if (analysisCellIds.length && !selectedIds.has(cell.id)) return;
          openCellImage(cell);
          if (onCellClick) onCellClick(cell);
          return;
        }
        if (onCellClick) onCellClick(cell);
      };

      polygon.addListener("click", handleCellSelect);
      polygon.addListener("mouseover", () => {
        if (!isAnalysisSelectable) return;
        polygon.setOptions(isSelected ? {
          strokeColor: "#B8FFE9",
          strokeOpacity: 1,
          strokeWeight: 2,
          zIndex: 4,
        } : GRID_CELL_HOVER_STYLE);
      });
      polygon.addListener("mouseout", () => {
        polygon.setOptions(baseCellStyle);
      });

      gridPolygonsRef.current.push(polygon);

      const line = new window.google.maps.Polyline({
        map: mapRef.current,
        path: [...expandedPaths, expandedPaths[0]],
        strokeOpacity: 0,
        strokeWeight: 1,
        strokeColor: "#0C1620",
        clickable: false,
        zIndex: 1,
        icons: [
          {
            icon: dashSymbol,
            offset: "0",
            repeat: "4px",
          },
        ],
      });

      gridLinesRef.current.push(line);
    });

    return () => {
      gridPolygonsRef.current.forEach((poly) => poly.setMap(null));
      gridPolygonsRef.current = [];
      gridLinesRef.current.forEach((line) => line.setMap(null));
      gridLinesRef.current = [];
    };
  }, [
    gridCells,
    selectedCellIds,
    analysisCellIds,
    mapRef,
    isMapReady,
    onCellClick,
    toggle,
    openCellImage,
  ]);


  const onMapClickRef = useRef(onMapClick);

  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  useEffect(() => {
    if (mapRef.current) {
      return;
    }

    const initMap = async () => {
      try {
        const waitForMapContainer = () =>
          new Promise((resolve, reject) => {
            let attempts = 0;
            const maxAttempts = 120;
            const check = () => {
              const el = mapElementRef.current;
              if (el && el.offsetWidth > 0 && el.offsetHeight > 0) {
                resolve(el);
                return;
              }
              attempts += 1;
              if (attempts >= maxAttempts) {
                reject(new Error("Map container not ready"));
                return;
              }
              requestAnimationFrame(check);
            };
            check();
          });

        const loader = new Loader({
          apiKey: GOOGLE_MAPS_API_KEY,
          version: "weekly",
          libraries: ["marker"],
        });

        await loader.load();

        const { Map } = await window.google.maps.importLibrary("maps");
        const { AdvancedMarkerElement } =
          await window.google.maps.importLibrary("marker");

        if (AdvancedMarkerElement) {
          if (!window.google.maps.marker) window.google.maps.marker = {};
          window.google.maps.marker.AdvancedMarkerElement =
            AdvancedMarkerElement;
          // console.log("AdvancedMarkerElement loaded successfully");
        } else {
          console.error("Failed to load AdvancedMarkerElement");
        }

        let initOptions = {
          center: defaultCenter,
          zoom: defaultZoom,
          mapTypeId: "satellite",
          mapId: "YOUR_MAP_ID",
          disableDefaultUI: true,
        };

        if (LOCK_MAP_INTERACTION) {
          const responsiveZoom = getResponsiveZoom(
            mapElementRef.current?.offsetWidth
          );
          initOptions = {
            ...initOptions,
            zoom: responsiveZoom,
            draggable: true,
            gestureHandling: "greedy",
            scrollwheel: true,
            disableDoubleClickZoom: false,
            keyboardShortcuts: true,
          };
        }

        const path = toPath(boundary);

        if (path.length >= 3) {
          const tmpBounds = new window.google.maps.LatLngBounds();
          path.forEach((p) => tmpBounds.extend(p));
          const centerLL = tmpBounds.getCenter();
          initOptions = {
            ...initOptions,
            center: { lat: centerLL.lat(), lng: centerLL.lng() },
          };
        }

        const container = await waitForMapContainer();
        const map = new Map(container, initOptions);

        if (LOCK_MAP_INTERACTION && path.length < 3) {
          applyLockedZoom(map);
        }

        mapRef.current = map;
        setIsMapReady(true);

        // console.log("Google Maps 초기화 완료");

        if (path.length >= 3 && !didFitRef.current && !LOCK_MAP_INTERACTION) {
          const bounds = new window.google.maps.LatLngBounds();
          path.forEach((p) => bounds.extend(p));
          map.fitBounds(bounds);
          didFitRef.current = true;
        }
      } catch (error) {
        console.error("Google Maps 초기화 실패:", error);
      }
    };

    initMap();
  }, []); // 빈 의존성 배열로 한 번만 실행

  useEffect(() => {
    if (!mapRef.current || !isMapReady) return;

    const listener = window.google.maps.event.addListener(
      mapRef.current,
      "click",
      (e) => {
        if (!e.latLng) return;
        onMapClickRef.current?.({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      }
    );

    return () => window.google.maps.event.removeListener(listener);
  }, [isMapReady]);


  return (
    <div className={toggle ? "drone-map is-analysis" : "drone-map"}>
      <div style={{ width: "100%", height: "100%", position: "relative" }}>
        <div
          ref={mapElementRef}
          className="drone-map-canvas"
          style={{ width: "100%", height: "100%" }}
        />
        <button
          type="button"
          className="map-fit-button"
          onClick={fitToFarmOverview}
          aria-label="Show full farm"
        >
          Fit
        </button>

        {/* <DroneTracker mapRef={mapRef} /> */}
        {toggle && (
          <div
            className={`ndvi-datetime${
              isTimeListOpen ? " ndvi-datetime--open" : ""
            }`}
          >
            <div className="ndvi-datetime-row">
              <button
                type="button"
                className={`ndvi-datetime-icon${!hasMissionDates ? " is-disabled" : ""}`}
                onClick={() => {
                  if (!hasMissionDates) {
                    toast.info("No analysis data available for this farm.");
                    return;
                  }
                  setIsDateOpen(true);
                }}
                aria-label="Open date picker"
              >
                <img src={calendarIcon} alt="" />
              </button>
              <span className="ndvi-datetime-text">
                {toggle
                  ? hasAppliedDate
                    ? `${
                        formatSelectedDate(selectedDate) || formatNow(now)
                      }${selectedMissionTime ? ` ${selectedMissionTime}` : ""}`
                    : formatSelectedDate(selectedDate) || formatNow(now)
                  : formatNow(now)}
              </span>
              <button
                type="button"
                className="ndvi-datetime-caret"
                aria-label="Date caret"
                onClick={handleToggleTimeList}
              >
                {"\u25BE"}
              </button>
            </div>
            {isTimeListOpen && (
              <div className="ndvi-time-list">
                {missionTimeList.length > 0 ? (
                  missionTimeList.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="ndvi-time-item"
                      onClick={() => handleSelectMissionTime(item)}
                    >
                      {item.label}
                    </button>
                  ))
                ) : (
                  <div className="ndvi-time-empty">No mission logs</div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="ndvi-container">
          <div className="ndvi-content">
            <NDVIToggle />
          </div>
        </div>
        {isCellImageOpen && (
          <div
            className="cell-image-overlay"
            onClick={() => {
              setIsCellImageOpen(false);
              setIsCellPreviewOpen(false);
            }}
          >
            <div
              className="cell-image-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cell-image-close"
                onClick={() => {
                  setIsCellImageOpen(false);
                  setIsCellPreviewOpen(false);
                }}
                aria-label="Close image"
              >
                X
              </button>
              {cellImageUrl && (
                <img
                  className="cell-image-main"
                  src={cellImageUrl}
                  alt="Crop detail"
                />
              )}
              {!cellImageUrl && !isCellImageLoading && (
                <div className="cell-image-empty">No image</div>
              )}
            </div>
          </div>
        )}
        {isCellImageOpen && cellThumbUrl && (
          <button
            type="button"
            className="cell-image-thumb"
            onClick={() => {
              setCellPreviewIndex(0);
              setIsGradCam(false);
              setIsCellPreviewOpen(true);
            }}
            aria-label="Open image preview"
          >
            <img src={cellThumbUrl} alt="Crop thumbnail" />
          </button>
        )}
        {isCellPreviewOpen && (
          <div
            className="cell-image-preview-overlay"
            onClick={() => setIsCellPreviewOpen(false)}
          >
            <div
              className="cell-image-preview-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cell-image-preview-header">
                <div className="cell-image-preview-title" />
                <button
                  type="button"
                  className="cell-image-preview-close"
                  onClick={() => setIsCellPreviewOpen(false)}
                  aria-label="Close preview"
                >
                  X
                </button>
              </div>
              <div className="cell-image-preview-body">
                <div className="cell-image-preview-image-wrap">
                  <button
                    type="button"
                    className="cell-image-preview-arrow left"
                    onClick={() =>
                      setCellPreviewIndex((prev) => {
                        const total = cellPreviewList.length;
                        if (!total) return 0;
                        return (prev - 1 + total) % total;
                      })
                    }
                    aria-label="Previous image"
                  >
                    {"\u25C0"}
                  </button>
                  <div className="cell-image-preview-main">
                    {(() => {
                      const items = cellPreviewList.length
                        ? cellPreviewList
                        : [];
                      const current = items[cellPreviewIndex] || {};
                      const previewSrc =
                        (isGradCam && current.gradcam) ||
                        current.crop ||
                        current.gradcam ||
                        "";
                      if (previewSrc) {
                        return <img src={previewSrc} alt="Crop preview" />;
                      }
                      return <div className="cell-image-preview-empty" />;
                    })()}
                  </div>
                  <button
                    type="button"
                    className="cell-image-preview-arrow right"
                    onClick={() =>
                      setCellPreviewIndex((prev) => {
                        const total = cellPreviewList.length;
                        if (!total) return 0;
                        return (prev + 1) % total;
                      })
                    }
                    aria-label="Next image"
                  >
                    {"\u25B6"}
                  </button>
                  <div className="cell-image-preview-side">
                    <label className="cell-image-preview-toggle">
                      <span>Grad-CAM</span>
                      <input
                        type="checkbox"
                        checked={isGradCam}
                        onChange={(e) => setIsGradCam(e.target.checked)}
                      />
                      <span className="toggle-track" />
                    </label>
                  </div>
                </div>
              </div>
              {(cellStatusLabel || cellStatusNote || cellCoordText) && (
                <div className="cell-image-preview-meta">
                  {cellStatusLabel && (
                    <div
                      className={`cell-image-preview-status status-${cellStatusTone}`}
                    >
                      <span className="status-dot" />
                      <span className="status-text">{cellStatusLabel}</span>
                    </div>
                  )}
                  {cellStatusNote && (
                    <div className="cell-image-preview-note">
                      {cellStatusNote}
                    </div>
                  )}
                  {cellCoordText && (
                    <div className="cell-image-preview-coord">
                      {cellCoordText}
                    </div>
                  )}
                </div>
              )}
              <div className="cell-image-preview-thumbs">
                {(() => {
                  const total = cellPreviewList.length;
                  const windowSize = 3;
                  const maxStart = Math.max(total - windowSize, 0);
                  const windowStart = Math.min(
                    Math.max(cellPreviewIndex - 1, 0),
                    maxStart
                  );
                  return Array.from({ length: windowSize }, (_, idx) => {
                    const item = cellPreviewList[windowStart + idx];
                    const src = item?.crop || item?.gradcam || "";
                    const isActive = windowStart + idx === cellPreviewIndex;
                  return (
                    <button
                      key={`thumb-${windowStart + idx}`}
                      type="button"
                      className={`cell-image-preview-thumb${
                        isActive ? " is-active" : ""
                      }`}
                      onClick={() => {
                        if (!item) return;
                        setCellPreviewIndex(windowStart + idx);
                      }}
                      aria-label={`Thumbnail ${idx + 1}`}
                    >
                      {src ? (
                        <img src={src} alt={`Thumbnail ${idx + 1}`} />
                      ) : (
                        <div className="cell-image-preview-thumb-empty" />
                      )}
                    </button>
                  );
                  });
                })()}
              </div>
            </div>
          </div>
        )}
        <NDVIDateModal
          isOpen={isDateOpen}
          value={selectedDate}
          missionDates={missionDates}
          onChange={(date) => date && setSelectedDate(date)}
          onClose={() => setIsDateOpen(false)}
          onApply={() => {
            setIsDateOpen(false);
            setSelectedMissionLogId(null);
            setSelectedMissionTime("");
            setMissionTimeList([]);
            setHasAppliedDate(true);
            fetchAnalysisCellIds();
          }}
        />
      </div>
    </div>
  );
});

export default DroneMap;








